// ============================================================================
// Tenant / company-employee model.
//
// All business data (invoices, clients, products, …) is keyed to the account
// OWNER's id. A business owner operates on their own id; an invited team member
// (manager, accountant, employee, comercial) operates on their EMPLOYER's id —
// so they see and work on the company's shared resources, not an empty silo.
//
// Use getOwnerId() to scope business-data reads/writes (the `user_id` column).
// Use the real auth user id only for "who did this" fields (audit, created_by)
// and for editing the person's OWN profile.
//
// The database RLS mirrors this: `user_id = auth.uid() OR user_id = get_employer_id()`.
// ============================================================================
import { supabase } from './supabase';

let _cache = null; // { uid, ownerId, employerId, role }
let _inflight = null; // shared promise while the profile lookup is in the air

// Drop the cached tenant (call on logout / when the active user changes).
export function clearTenantCache() {
  _cache = null;
  _inflight = null;
}

// The signed-in user, WITHOUT a network round trip when a session is already
// held locally.
//
// supabase-js's auth.getUser() calls /auth/v1/user on EVERY invocation. This
// module sits on the mount path of practically every page (nav, route guards,
// each page's own load, each dashboard widget), so a single page load stacked up
// half a dozen serialized ~150 ms auth calls before any data was even requested.
// getSession() answers from local storage — and refreshes the token itself when
// it has expired — so the token we hand on is just as valid.
//
// This is not a weaker check: the user id is only ever used to SCOPE queries.
// Postgres RLS re-validates the JWT server-side on every request, so a tampered
// local session still cannot read another tenant's rows.
//
// Falls back to getUser() when there is no local session, so the "logged out"
// answer stays exactly what it was before.
export async function getAuthUser() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) return session.user;
  } catch { /* fall through to the authoritative check */ }
  const { data: { user } } = await supabase.auth.getUser();
  return user || null;
}

// Resolve the current user plus the account owner they operate under.
// Returns { user, ownerId, employerId, role, isTeamMember }.
export async function getTenant() {
  const user = await getAuthUser();
  if (!user) {
    return { user: null, ownerId: null, employerId: null, role: null, isTeamMember: false };
  }
  if (_cache && _cache.uid === user.id) {
    return { user, ownerId: _cache.ownerId, employerId: _cache.employerId, role: _cache.role, isTeamMember: !!_cache.employerId };
  }
  // Several components call this at once on a cold cache; share one lookup
  // rather than firing the same profiles query five times in parallel.
  if (!_inflight || _inflight.uid !== user.id) {
    const entry = { uid: user.id };
    entry.promise = supabase
      .from('profiles')
      .select('employer_id, role')
      .eq('id', user.id)
      .single()
      .then(({ data: profile }) => {
        const employerId = profile?.employer_id || null;
        const resolved = { uid: user.id, ownerId: employerId || user.id, employerId, role: profile?.role || null };
        // Safe to write even if clearTenantCache() ran mid-flight (admin
        // impersonation swaps the session): the entry is keyed by uid and every
        // read above checks it, so a leftover entry for the previous user is
        // inert rather than wrong — the next caller simply refetches.
        _cache = resolved;
        return resolved;
      })
      // Only retire this entry if it's still the current one.
      .finally(() => { if (_inflight === entry) _inflight = null; });
    _inflight = entry;
  }
  const c = await _inflight.promise;
  return { user, ownerId: c.ownerId, employerId: c.employerId, role: c.role, isTeamMember: !!c.employerId };
}

// Convenience: the account owner id to scope business data by.
export async function getOwnerId() {
  return (await getTenant()).ownerId;
}
